import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';
import MultiSelect from '../../components/MultiSelect';

const DMC_ITINERARY_FORM_CACHE_KEY = 'dmc_itinerary_form_cache_v1';
const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || '';
const ADULT_OPTIONS = Array.from({ length: 20 }, (_, index) => String(index + 1));
const CHILD_OPTIONS = Array.from({ length: 21 }, (_, index) => String(index));

const buildDurationLabel = (days) => {
  const dayText = `${days} ${days === 1 ? 'Day' : 'Days'}`;
  const nights = Math.max(days - 1, 0);
  const nightText = `${nights} ${nights === 1 ? 'Night' : 'Nights'}`;

  return `${dayText} ${nightText}`;
};

const DEFAULT_DURATION_OPTIONS = Array.from(
  { length: 30 },
  (_, index) => buildDurationLabel(index + 1)
);

const formatLocationName = (location) => {
  if (!location) return '';

  const stateName = location?.state?.[0]?.name || location?.stateId?.name;
  const countryName = location?.country?.[0]?.name || location?.countryId?.name;
  return [location.name, stateName, countryName].filter(Boolean).join(', ');
};

const normalizeLocations = (locationList = []) => (
  locationList.map((location) => ({
    ...location,
    name: formatLocationName(location) || location.name
  }))
);

const normalizeDmcs = (dmcList = []) => (
  dmcList.map((dmc) => ({
    ...dmc,
    name: [dmc.name, dmc.phone].filter(Boolean).join(' - ')
  }))
);

const readDmcItineraryCache = () => {
  try {
    const cached = sessionStorage.getItem(DMC_ITINERARY_FORM_CACHE_KEY);
    return cached ? JSON.parse(cached) : { dmcs: [], locations: [] };
  } catch (error) {
    console.error('Failed to read DMC itinerary form cache:', error);
    return { dmcs: [], locations: [] };
  }
};

const writeDmcItineraryCache = (payload = {}) => {
  try {
    sessionStorage.setItem(DMC_ITINERARY_FORM_CACHE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error('Failed to write DMC itinerary form cache:', error);
  }
};

const getPdfUrl = (path = '') => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${MEDIA_URL}${path}`;
};

const withCurrentValue = (options, currentValue) => {
  if (!currentValue || options.includes(String(currentValue))) {
    return options;
  }

  return [String(currentValue), ...options];
};

const EditDMCItinerary = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const cachedFormOptions = !isEditMode ? readDmcItineraryCache() : { dmcs: [], locations: [] };

  const [loading, setLoading] = useState(
    isEditMode || ((cachedFormOptions.dmcs || []).length === 0 && (cachedFormOptions.locations || []).length === 0)
  );
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    duration: '',
    adult: '',
    child: '',
    dmc: '',
    locationCover: [],
    pdfLink: '',
    itinerary: ''
  });
  const [dmcs, setDmcs] = useState(normalizeDmcs(cachedFormOptions.dmcs || []));
  const [locations, setLocations] = useState(normalizeLocations(cachedFormOptions.locations || []));
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [existingPdf, setExistingPdf] = useState('');
  const durationOptions = withCurrentValue(DEFAULT_DURATION_OPTIONS, formData.duration);
  const adultOptions = withCurrentValue(ADULT_OPTIONS, formData.adult);
  const childOptions = withCurrentValue(CHILD_OPTIONS, formData.child);

  useEffect(() => {
    fetchFormData();
  }, [id]);

  const fetchFormData = async () => {
    const hasCachedOptions = !isEditMode && (
      (cachedFormOptions.dmcs || []).length > 0 || (cachedFormOptions.locations || []).length > 0
    );

    try {
      if (!hasCachedOptions || isEditMode) {
        setLoading(true);
      }

      const response = isEditMode
        ? await apiService.get(`editDmcItinerary/${id}`)
        : await apiService.get('addDmcItinerary');

      const responseData = response?.data || {};
      const nextCachePayload = {
        dmcs: responseData.dmcs || [],
        locations: responseData.locations || []
      };

      if (nextCachePayload.dmcs.length > 0 || nextCachePayload.locations.length > 0) {
        writeDmcItineraryCache(nextCachePayload);
      }

      setDmcs(normalizeDmcs(nextCachePayload.dmcs.length > 0 ? nextCachePayload.dmcs : (cachedFormOptions.dmcs || [])));
      setLocations(normalizeLocations(nextCachePayload.locations.length > 0 ? nextCachePayload.locations : (cachedFormOptions.locations || [])));

      if (isEditMode) {
        const data = responseData.data;

        if (!data) {
          toast.error('DMC itinerary not found');
          navigate('/view-dmc-itineraries');
          return;
        }

        setFormData({
          id: data._id || '',
          duration: data.duration || '',
          adult: data.adult ?? '',
          child: data.child ?? '',
          dmc: data.dmc?._id || data.dmc || '',
          locationCover: Array.isArray(data.locationCover) ? data.locationCover.map((item) => item._id) : [],
          pdfLink: data.pdfLink || '',
          itinerary: data.itinerary || ''
        });
        setExistingPdf(data.pdfUpload || '');
      }
    } catch (error) {
      console.error('DMC itinerary fetch error:', error);
      if (isEditMode) {
        toast.error('Failed to load DMC itinerary data');
        navigate('/view-dmc-itineraries');
      } else if (!hasCachedOptions) {
        toast.error('Failed to load DMC itinerary data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePdfChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSelectedPdf(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.dmc) {
      toast.error('Please select a DMC');
      return;
    }

    try {
      setSubmitting(true);

      const submitData = new FormData();
      if (formData.id) submitData.append('id', formData.id);
      submitData.append('duration', formData.duration);
      submitData.append('adult', formData.adult);
      submitData.append('child', formData.child);
      submitData.append('dmc', formData.dmc);
      submitData.append('pdfLink', formData.pdfLink || '');
      submitData.append('itinerary', formData.itinerary || '');

      formData.locationCover.forEach((locationId) => {
        submitData.append('locationCover', locationId);
      });

      if (selectedPdf) {
        submitData.append('doc', selectedPdf);
      }

      const response = await apiService.postFormData('saveDmcItinerary', submitData);

      if (response.status) {
        toast.success(response.message || `DMC itinerary ${isEditMode ? 'updated' : 'added'} successfully`);
        navigate('/view-dmc-itineraries');
      } else {
        toast.error(response.message || 'Failed to save DMC itinerary');
      }
    } catch (error) {
      console.error('DMC itinerary save error:', error);
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  const existingPdfUrl = getPdfUrl(existingPdf);

  return (
    <div className="p-0">
      <div className="mb-3">
        <nav className="flex flex-wrap items-center text-xs sm:text-sm">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700 no-underline">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/view-dmc-itineraries" className="text-gray-500 hover:text-gray-700 no-underline">DMC Itinerary</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">{isEditMode ? 'Edit DMC Itinerary' : 'Add DMC Itinerary'}</span>
        </nav>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#009ef7' }}></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6 sm:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                DMC <span className="text-red-500">*</span>
              </label>
              <select
                name="dmc"
                value={formData.dmc}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
                required
              >
                <option value="">Select DMC</option>
                {dmcs.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration <span className="text-red-500">*</span>
              </label>
              <select
                name="duration"
                value={formData.duration}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
                required
              >
                <option value="">Select duration</option>
                {durationOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adult <span className="text-red-500">*</span>
              </label>
              <select
                name="adult"
                value={formData.adult}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
                required
              >
                <option value="">Select adult count</option>
                {adultOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Child
              </label>
              <select
                name="child"
                value={formData.child}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
              >
                <option value="">Select child count</option>
                {childOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Location Cover</label>
              <MultiSelect
                options={locations}
                value={formData.locationCover}
                onChange={(value) => setFormData((prev) => ({ ...prev, locationCover: value }))}
                placeholder="Select location cover"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">PDF Upload</label>
              <input
                type="file"
                accept="application/pdf"
                onChange={handlePdfChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
              />
              {selectedPdf ? (
                <p className="mt-2 text-xs text-gray-500">{selectedPdf.name}</p>
              ) : null}
              {!selectedPdf && existingPdfUrl ? (
                <a
                  href={existingPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-sm text-[#009ef7] hover:underline"
                >
                  View current uploaded PDF
                </a>
              ) : null}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">PDF Link</label>
              <input
                type="url"
                name="pdfLink"
                value={formData.pdfLink}
                onChange={handleInputChange}
                placeholder="Enter PDF link"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Itinerary</label>
              <textarea
                name="itinerary"
                value={formData.itinerary}
                onChange={handleInputChange}
                placeholder="Enter itinerary details"
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8">
            <button
              type="button"
              onClick={() => navigate('/view-dmc-itineraries')}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50"
              style={{ backgroundColor: '#009ef7' }}
            >
              {submitting ? 'Please wait...' : (isEditMode ? 'Update DMC Itinerary' : 'Save DMC Itinerary')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default EditDMCItinerary;
